import { useFormContext, Controller } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '../../lib/utils';
import { ArrowDownIcon } from '../../assets/ArrowDown';
import { Input, InputProps } from '../ui/input';
import { useDigitsMask } from './useDigitsMask';

const PHONE_DIGITS = 9;

/** «90 123 4567» — оператор, три и четыре цифры. */
const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, PHONE_DIGITS);
  return digits.replace(/(\d{2})(\d{3})(\d{0,4})/, (_, p1, p2, p3) => {
    return `${p1} ${p2}${p3 ? ' ' + p3 : ''}`;
  });
};

/** Поле с маской вынесено, чтобы хук с кареткой жил внутри render-пропа. */
const MaskedPhoneInput = ({
  value,
  onChange,
  inputRef,
  ...props
}: Omit<InputProps, 'value' | 'onChange'> & {
  value: string;
  onChange: (digits: string) => void;
  inputRef: (el: HTMLInputElement | null) => void;
}) => {
  const mask = useDigitsMask({
    digits: value,
    format: formatPhone,
    maxLength: PHONE_DIGITS,
    onChange,
  });

  return (
    <Input
      {...props}
      ref={el => {
        mask.inputRef.current = el;
        inputRef(el);
      }}
      value={mask.value}
      onChange={mask.onChange}
      onKeyDown={mask.onKeyDown}
    />
  );
};

interface IProps extends InputProps {
  name: string;
  label?: string;
  required?: boolean;
}

export default function PhoneField({
  name,
  label,
  required,
  disabled,
  className,
  onBlur,
  ...rest
}: IProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="w-full">
          {label && (
            <FormLabel
              className={cn(
                'transition-all text-body-base-regular',
                fieldState.error && 'text-error',
                !fieldState.error && field.value && 'text-muted-foreground'
              )}
            >
              {required && <span className="text-red-500">*</span>}
              {label}
            </FormLabel>
          )}
          <FormControl>
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <MaskedPhoneInput
                  {...rest}
                  name={field.name}
                  inputRef={field.ref}
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={e => {
                    field.onBlur(); // react-hook-form tracking
                    onBlur?.(e); // call parent onBlur (like setOpen(false))
                  }}
                  iconLeft={
                    <div className="flex flex-row space-x-2 items-center w-[120px]">
                      <img
                        className="w-7 h-[22px] object-cover"
                        src="/assets/flag.png"
                        alt="flag"
                      />
                      <ArrowDownIcon className="text-text-primary rotate-180" />
                      <p className="text-display-lg text-text-tertiary pl-1">
                        +998
                      </p>
                    </div>
                  }
                  disabled={disabled}
                  className={cn(
                    'shadow-none bg-fill-base rounded-[16px]',
                    fieldState.error
                      ? 'border border-fill-danger'
                      : field.value
                        ? 'border border-success'
                        : 'border border-border placeholder:text-body-base-regular',
                    className // ⬅ gets passed from props like max-w-[343px]
                  )}
                />
              )}
            />
          </FormControl>
          <FormMessage className="text-caption-sm-medium text-fill-danger pl-1" />
        </FormItem>
      )}
    />
  );
}
